/**
 * Pointer drag-scrolling for wide table surfaces.
 *
 * Wide reports can only be panned with the horizontal scrollbar, which is hard
 * to reach on long pages. This lets a mouse grab the table and pan it. Touch
 * and pen keep their native behaviour, and clicks are only swallowed when the
 * pointer actually travelled.
 */

const SCROLLER_SELECTOR = [
  ".table-responsive",
  ".custom-datatable-filter",
  ".dq-table-wrap",
  ".ant-table-content",
  ".ant-table-body",
  "[data-drag-scroll]",
].join(",");

const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "label",
  "summary",
  "[contenteditable='']",
  "[contenteditable='true']",
  "[role='button']",
  "[data-bs-toggle]",
  ".ant-select",
  ".ant-picker",
  ".ant-checkbox",
  ".ant-radio",
  ".ant-switch",
  ".ant-slider",
  ".ant-pagination",
].join(",");

const DRAG_THRESHOLD = 5;
const DRAGGING_CLASS = "dq-drag-scrolling";

let session = null;

function canScrollHorizontally(element) {
  if (!(element instanceof HTMLElement)) return false;
  if (element.scrollWidth - element.clientWidth <= 1) return false;
  const overflowX = window.getComputedStyle(element).overflowX;
  return overflowX === "auto" || overflowX === "scroll" || overflowX === "overlay";
}

function findScroller(target) {
  let node = target instanceof Element ? target.closest(SCROLLER_SELECTOR) : null;

  while (node) {
    if (canScrollHorizontally(node)) return node;
    node = node.parentElement?.closest(SCROLLER_SELECTOR) || null;
  }

  return null;
}

function suppressNextClick() {
  const swallow = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  document.addEventListener("click", swallow, { capture: true, once: true });
  window.setTimeout(() => {
    document.removeEventListener("click", swallow, { capture: true });
  }, 0);
}

function endSession() {
  if (!session) return;

  const wasDragging = session.dragging;
  document.removeEventListener("pointermove", onPointerMove, true);
  document.removeEventListener("pointerup", onPointerUp, true);
  document.removeEventListener("pointercancel", onPointerUp, true);
  document.body.classList.remove(DRAGGING_CLASS);
  session = null;

  if (wasDragging) suppressNextClick();
}

function onPointerMove(event) {
  if (!session || event.pointerId !== session.pointerId) return;

  const deltaX = event.clientX - session.startX;
  const deltaY = event.clientY - session.startY;

  if (!session.dragging) {
    if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
    // A mostly vertical gesture belongs to the page, not the table.
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      endSession();
      return;
    }

    session.dragging = true;
    document.body.classList.add(DRAGGING_CLASS);
  }

  event.preventDefault();
  session.container.scrollLeft = session.scrollLeft - deltaX;
}

function onPointerUp(event) {
  if (!session || event.pointerId !== session.pointerId) return;
  endSession();
}

function onPointerDown(event) {
  if (session) endSession();
  if (event.pointerType !== "mouse" || event.button !== 0) return;
  if (!(event.target instanceof Element)) return;
  if (event.target.closest(INTERACTIVE_SELECTOR)) return;

  const container = findScroller(event.target);
  if (!container) return;

  session = {
    container,
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    scrollLeft: container.scrollLeft,
    dragging: false,
  };

  document.addEventListener("pointermove", onPointerMove, true);
  document.addEventListener("pointerup", onPointerUp, true);
  document.addEventListener("pointercancel", onPointerUp, true);
}

export function initTableDragScroll() {
  if (typeof window === "undefined" || window.__dqDragScrollReady) return;

  window.__dqDragScrollReady = true;
  document.addEventListener("pointerdown", onPointerDown, true);
  window.addEventListener("blur", endSession);
}
